// ---------------------------------------------------------------------------
// New-signal notification sound.
//
// A short, ICQ-style two-note "uh-oh" chime synthesised on the fly via the
// Web Audio API so we do not have to ship (or license) an MP3 asset.
//
// Two short sine-wave tones, the second a perfect fourth below the first:
//   E5 (659.25 Hz) → B4 (493.88 Hz)
// Each note is shaped with a small attack/release envelope so it does not
// "click" on start/end. Total duration ≈ 320 ms.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'pipmaster:notificationSoundEnabled';

let cachedContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (cachedContext) return cachedContext;
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  cachedContext = new Ctor();
  return cachedContext;
}

function playTone(
  ctx: AudioContext,
  frequency: number,
  startOffset: number,
  duration: number,
  peakGain = 0.18,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = frequency;

  const startAt = ctx.currentTime + startOffset;
  const endAt = startAt + duration;

  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(peakGain, startAt + 0.02);
  gain.gain.linearRampToValueAtTime(peakGain, endAt - 0.05);
  gain.gain.linearRampToValueAtTime(0, endAt);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start(startAt);
  osc.stop(endAt);
}

/** Returns true when the user has opted-in to the new-signal sound. Default ON. */
export function isNotificationSoundEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) return true;
  return raw === 'true';
}

export function setNotificationSoundEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
}

/**
 * Play the new-signal chime, but only if the user has not disabled it.
 * Safe to call from anywhere — no-ops on the server, when AudioContext is
 * not available, or when the user has opted out.
 */
export function playNewSignalSound(): void {
  if (!isNotificationSoundEnabled()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  // AudioContext may start in "suspended" state until the user has
  // interacted with the page. resume() is a no-op once it is already
  // running and silently fails if the policy blocks playback.
  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => undefined);
  }

  // Two-note ICQ-style chime
  playTone(ctx, 659.25, 0, 0.14); // E5
  playTone(ctx, 493.88, 0.16, 0.18); // B4
}
